<template>
  <el-container class="chat-page">
    <resizable-aside :default-width="260" :min-width="200" :max-width="500" storage-key="chat-aside-width">
      <div class="header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
        <el-input class="search-text" size="small" placeholder="搜索" v-model="searchText">
          <i class="el-icon-search el-input__icon" slot="prefix"> </i>
        </el-input>
      </div>

      <div
        class="chat-loading"
        v-if="loading"
        v-loading="true"
        element-loading-text="消息接收中..."
        element-loading-spinner="el-icon-loading"
        element-loading-background="#F9F9F9"
        element-loading-size="24"
      >
      </div>

      <virtual-scroller class="scroll-box" :items="showChats">
        <template v-slot="{ item }">
          <!-- 左侧会话列表 -->
          <chat-item
            :chat="item"
            @click.native="onActiveItem(item)"
            @delete="onDelItem(item)"
            @info="onShowInfo(item)"
            @dnd="onDnd(item)"
            @top="onTop(item)"
            :active="isActiveChat(item)"
          ></chat-item>
        </template>
      </virtual-scroller>
    </resizable-aside>

    <el-container class="chat-box">
      <!-- 关键：加了 :key，避免缓存导致第二次第三方进入无法发送消息 -->
      <chat-box
        v-if="activeChat && activeChat.type != 'SYSTEM'"
        :chat="activeChat"
        :key="activeChat.type + '-' + activeChat.targetId"
      ></chat-box>

      <chat-system-box
        v-if="activeChat && activeChat.type == 'SYSTEM'"
        :chat="activeChat"
        :key="'sys-' + activeChat.targetId"
      ></chat-system-box>
    </el-container>
  </el-container>
</template>

<script>
import ChatItem from "../components/chat/ChatItem.vue";
import ChatBox from "../components/chat/ChatBox.vue";
import ChatSystemBox from "../components/chat/ChatSystemBox.vue";
import ResizableAside from "../components/common/ResizableAside.vue";
import VirtualScroller from "../components/common/VirtualScroller.vue";

export default {
  name: "chat",
  components: {
    ChatItem,
    ChatBox,
    ChatSystemBox,
    ResizableAside,
    VirtualScroller,
  },
  data() {
    return {
      searchText: "",
    };
  },
  computed: {
    activeChat() {
      const active = this.chatStore.activeChat;
      if (!active) return null;

      // 始终优先使用当前 chats 数组里的规范会话对象，
      // 避免 activeChat 指向旧缓存对象导致消息区与左侧列表不同步。
      const current = this.chatStore.chats.find(
        (chat) =>
          chat &&
          String(chat.type) === String(active.type) &&
          String(chat.targetId) === String(active.targetId)
      );

      return current || active;
    },
    loading() {
      return this.chatStore.loading;
    },
    showChats() {
      return this.chatStore.chats.filter((chat) => this.isShowChat(chat));
    },
  },
  methods: {
    isActiveChat(chat) {
      const active = this.activeChat;
      if (!chat || !active) return false;

      return (
        String(chat.type) === String(active.type) &&
        String(chat.targetId) === String(active.targetId)
      );
    },

    /**
     * 根据路由精确激活聊天。
     *
     * 手机版的核心规则同步到 PC：
     *
     * customerId=1 / 3 / 4 是固定指定 PRIVATE 会话：
     * - 1 商务
     * - 3 代理
     * - 4 客服
     *
     * 只要带了这些 customerId，就绝不能相信上一次 activeChat，
     * 也不能退回其它会话。
     *
     * PC 的 chatStore.openChat() 是 async，
     * 所以这里必须 await，否则 Promise/旧 activeChat 都可能造成串聊天。
     */
    async autoActiveChatFromRoute() {
      const customerIdText = String(this.$route.query.customerId || "");
      const forceCustomerChat =
        customerIdText === "1" ||
        customerIdText === "3" ||
        customerIdText === "4";

      const id = forceCustomerChat
        ? customerIdText
        : this.$route.query.id;

      if (!id) return;

      const targetId = Number(id);
      if (!targetId) return;

      /**
       * 记录本次请求目标。
       * 如果等待 store 的过程中又来了新的 customerId，
       * 旧异步流程结束后也不能覆盖新的聊天。
       */
      const requestedCustomerId = forceCustomerChat
        ? customerIdText
        : "";

      // 固定 customerId 入口必须等好友和聊天缓存加载完成。
      if (
        forceCustomerChat &&
        (
          this.chatStore.loading ||
          this.chatStore.blockSave
        )
      ) {
        setTimeout(() => this.autoActiveChatFromRoute(), 200);
        return;
      }

      let exactChat = this.chatStore.chats.find(
        (item) =>
          item &&
          item.type === "PRIVATE" &&
          String(item.targetId) === String(targetId)
      );

      if (!exactChat) {
        const friend = this.friendStore.findFriend(targetId);

        if (!friend) {
          if (forceCustomerChat) {
            // 与手机版一致：固定客服会话还没准备好时只等待，
            // 不允许退回旧的代理/客服会话。
            setTimeout(() => this.autoActiveChatFromRoute(), 200);
            return;
          }

          console.warn("⚠️ autoActiveChatFromRoute: 未找到好友", targetId);
          return;
        }

        exactChat = await this.chatStore.openChat({
          type: "PRIVATE",
          targetId: friend.id,
          showName: friend.showNickName || friend.nickName || "客服",
          headImage: friend.headImage || "",
          companyName: friend.companyName || "",
        });
      }

      if (!exactChat) {
        if (forceCustomerChat) {
          setTimeout(() => this.autoActiveChatFromRoute(), 200);
        }
        return;
      }

      /**
       * 防止快速连续点“代理(customerId=3)”和“客服(customerId=4)”时，
       * 前一次异步结果回来得更晚，把后一次聊天覆盖掉。
       */
      if (forceCustomerChat) {
        const latestCustomerId = String(
          this.$route.query.customerId || ""
        );

        if (latestCustomerId !== requestedCustomerId) {
          console.log("[THIRD_CHAT][PC] stale target ignored =>", {
            requestedCustomerId,
            latestCustomerId,
          });
          return;
        }

        if (
          exactChat.type !== "PRIVATE" ||
          String(exactChat.targetId) !== requestedCustomerId
        ) {
          console.warn("[THIRD_CHAT][PC] wrong chat rejected =>", {
            requestedCustomerId,
            chatType: exactChat.type,
            targetId: exactChat.targetId,
          });
          setTimeout(() => this.autoActiveChatFromRoute(), 200);
          return;
        }
      }

      this.chatStore.setActiveChat(exactChat);

      console.log("[THIRD_CHAT][PC] exact chat activated =>", {
        customerId: forceCustomerChat ? requestedCustomerId : "",
        targetId: exactChat.targetId,
        type: exactChat.type,
      });
    },

    /* 左侧点击会话 */
    onActiveItem(chat) {
      this.chatStore.setActiveChat(chat);
    },

    onDelItem(chat) {
      this.chatStore.removeChat(chat);
    },

    onShowInfo(chat) {
      if (chat.type == "PRIVATE") {
        this.$router.push("/home/friend?id=" + chat.targetId);
      } else if (chat.type == "GROUP") {
        if (!this.groupStore.isGroup(chat.targetId)) {
          this.$message.error("您已不在群聊中，无法查看群资料");
          return;
        }
        this.$router.push("/home/group?id=" + chat.targetId);
      }
    },

    onDnd(chat) {
      if (chat.type == "PRIVATE") {
        this.setFriendDnd(chat, chat.targetId, !chat.isDnd);
      } else {
        this.setGroupDnd(chat, chat.targetId, !chat.isDnd);
      }
    },

    setFriendDnd(chat, friendId, isDnd) {
      let formData = {
        friendId: friendId,
        isDnd: isDnd,
      };
      this.$http({
        url: "/friend/dnd",
        method: "put",
        data: formData,
      }).then(() => {
        this.friendStore.setDnd(friendId, isDnd);
        this.chatStore.setDnd(chat, isDnd);
      });
    },

    setGroupDnd(chat, groupId, isDnd) {
      let formData = {
        groupId: groupId,
        isDnd: isDnd,
      };
      this.$http({
        url: "/group/dnd",
        method: "put",
        data: formData,
      }).then(() => {
        this.groupStore.setDnd(groupId, isDnd);
        this.chatStore.setDnd(chat, isDnd);
      });
    },

    onTop(chat) {
      if (chat.type == "PRIVATE") {
        this.setFriendTop(chat, chat.targetId, !chat.isTop);
      } else {
        this.setGroupTop(chat, chat.targetId, !chat.isTop);
      }
    },

    setFriendTop(chat, friendId, isTop) {
      let formData = {
        friendId: friendId,
        isTop: isTop,
      };
      this.$http({
        url: "/friend/top",
        method: "put",
        data: formData,
      }).then(() => {
        this.friendStore.setTop(friendId, isTop);
        this.chatStore.setTop(chat, isTop);
      });
    },

    setGroupTop(chat, groupId, isTop) {
      let formData = {
        groupId: groupId,
        isTop: isTop,
      };
      this.$http({
        url: "/group/top",
        method: "put",
        data: formData,
      }).then(() => {
        this.groupStore.setTop(groupId, isTop);
        this.chatStore.setTop(chat, isTop);
      });
    },

    isShowChat(chat) {
      return !this.searchText || chat.showName.includes(this.searchText);
    },
  },
  mounted() {
    /**
     * 再次以当前路由 customerId 为准设置固定目标。
     * Login.vue 已经提前设置一次，这里做第二层保险。
     */
    const customerId = String(this.$route.query.customerId || "");

    if (
      customerId === "1" ||
      customerId === "3" ||
      customerId === "4"
    ) {
      sessionStorage.setItem("thirdFixedCustomerId", customerId);
    } else {
      sessionStorage.removeItem("thirdFixedCustomerId");
    }

    // 进入聊天页时，按 customerId 精确激活会话
    this.autoActiveChatFromRoute();
  },

  beforeDestroy() {
    // 离开聊天页后解除第三方固定目标，不影响普通 PC 聊天。
    sessionStorage.removeItem("thirdFixedCustomerId");
  },
  watch: {
    // 离线消息刷新完成后再按当前路由重新绑定一次规范会话对象。
    // Home.vue 是先 loading=false，再 refreshChats()；watch 会在下一轮更新时执行，
    // 此时 this.chatStore.chats 已经是最终列表。
    loading(newLoading, oldLoading) {
      if (oldLoading && !newLoading) {
        const customerId = String(this.$route.query.customerId || "");
        if (["1", "3", "4"].includes(customerId)) {
          this.$nextTick(() => this.autoActiveChatFromRoute());
        }
      }
    },

    // 普通 PC 路由仍保持原 id 行为。
    "$route.query.id"(newId) {
      if (newId) {
        this.autoActiveChatFromRoute();
      }
    },

    /**
     * 第三方入口直接监听 customerId。
     * customerId=3 与 customerId=4 连续切换时，
     * 每次都重新按目标 ID 精确激活，不能相互覆盖。
     */
    "$route.query.customerId"(newCustomerId) {
      const customerId = String(newCustomerId || "");

      if (
        customerId === "1" ||
        customerId === "3" ||
        customerId === "4"
      ) {
        /**
         * customerId=4 -> customerId=3 连续切换时，
         * 必须先覆盖 sessionStorage 里的固定目标，再重新激活。
         */
        sessionStorage.setItem("thirdFixedCustomerId", customerId);
        this.autoActiveChatFromRoute();
      } else {
        sessionStorage.removeItem("thirdFixedCustomerId");
      }
    },
  },
};
</script>

<style lang="scss">
.chat-page {
  .header {
    height: 60px;
    display: flex;
    align-items: center;
    padding: 0 12px;
  }

  .chat-loading {
    height: 50px;
    background-color: #eee;

    .el-icon-loading {
      font-size: 24px;
      color: var(--im-text-color-light);
    }

    .el-loading-text {
      color: var(--im-text-color-light);
    }

    .chat-loading-box {
      height: 100%;
    }
  }

  .chat-items {
    flex: 1;
  }
}
</style>
